import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDarkMode } from '../contexts/DarkModeContext';
import { CheckCircle, XCircle, AlertCircle, X, Info } from 'lucide-react';

// Toast Context
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Individual Toast Component
const ToastItem = ({ id, type, message, onClose, duration = 4000, action = null }) => {
  const { isDarkMode } = useDarkMode();
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 200); // Match animation duration
  }, [id, onClose]);

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    error: <XCircle className="w-5 h-5 text-red-500" />,
    warning: <AlertCircle className="w-5 h-5 text-amber-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />,
  };

  const borderColors = {
    success: isDarkMode ? 'border-green-500/30' : 'border-green-500/40',
    error: isDarkMode ? 'border-red-500/30' : 'border-red-500/40',
    warning: isDarkMode ? 'border-amber-500/30' : 'border-amber-500/40',
    info: isDarkMode ? 'border-blue-500/30' : 'border-blue-500/40',
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border
        transform transition-all duration-200 ease-out
        ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}
        ${isDarkMode ? 'bg-[#1e293b] text-white' : 'bg-white text-gray-900'}
        ${borderColors[type]}
      `}
      style={{
        minWidth: '300px',
        maxWidth: '420px',
      }}
    >
      <div className="flex-shrink-0">
        {icons[type]}
      </div>
      <p className={`flex-1 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
        {message}
      </p>
      {action && (
        <button
          onClick={() => { action.onClick(); handleClose(); }}
          className={`flex-shrink-0 text-xs font-medium px-2 py-1 rounded-md transition-colors ${
            isDarkMode
              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'
              : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
          }`}
        >
          {action.label}
        </button>
      )}
      <button
        onClick={handleClose}
        className={`flex-shrink-0 p-1 rounded-md transition-colors ${
          isDarkMode
            ? 'hover:bg-slate-700 text-gray-400 hover:text-gray-200'
            : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
        }`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

ToastItem.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  duration: PropTypes.number,
  action: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }),
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div 
      className="fixed top-4 right-4 z-[10000] flex flex-col gap-2"
      style={{ pointerEvents: 'none' }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} style={{ pointerEvents: 'auto' }}>
          <ToastItem
            id={toast.id}
            type={toast.type}
            message={toast.message}
            duration={toast.duration}
            action={toast.action}
            onClose={removeToast}
          />
        </div>
      ))}
    </div>
  );
};

ToastContainer.propTypes = {
  toasts: PropTypes.array.isRequired,
  removeToast: PropTypes.func.isRequired,
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, message, duration = 4000, action = null) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setToasts((prev) => [...prev, { id, type, message, duration, action }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toast = {
    success: (message, duration, action) => addToast('success', message, duration, action),
    error: (message, duration, action) => addToast('error', message, duration, action),
    warning: (message, duration, action) => addToast('warning', message, duration, action),
    info: (message, duration, action) => addToast('info', message, duration, action),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ToastProvider;